import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, InputAdornment, LinearProgress, Paper, Stack, TextField, Typography, alpha } from '@mui/material';
import { CloudDoneOutlined, Search, MenuBookOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { timetableService, Lesson, LessonRules, LessonsOverview } from '../../../services/timetableService';
import { fmtClassName } from '../../../utils/pdf/format';
import FilterBar from '../../common/FilterBar';
import EmptyState from '../../common/EmptyState';
import { fixLink, weeklyPeriods } from '../timetableUtils';
import LessonRow, { LessonHeader, LessonRowData } from './LessonRow';
import EntityList, { EntityItem } from '../board/EntityList';
import TeacherLoadCard from './TeacherLoadCard';

interface LessonsTabProps {
  sessionId: string;
  overview: LessonsOverview;
  canWrite: boolean;
  /** Called after a rule is saved, so readiness checks can refresh. */
  onChanged: () => void;
}

interface ClassGroup {
  classId: string;
  label: string;
  order: number;
  sections: { key: string; name: string | null; load: number }[];
  rows: LessonRowData[];
}

const applyRules = (l: Lesson, r: LessonRules): Lesson => ({
  ...l,
  // A split subject's totals change; each teacher's share stays until Teaching Loads updates it.
  ...(r.periodsPerWeek !== undefined && (l.split ? { groupCount: r.periodsPerWeek } : { count: r.periodsPerWeek, groupCount: r.periodsPerWeek })),
  ...(r.doubles !== undefined && (l.split ? { groupDoubles: r.doubles } : { doubles: r.doubles, groupDoubles: r.doubles })),
  ...(r.maxPerDay !== undefined && { maxPerDay: r.maxPerDay }),
  ...(r.roomType !== undefined && { roomType: r.roomType }),
  ...(r.preferredTime !== undefined && { preferredTime: r.preferredTime }),
});

/** Every subject each class is taught, with who teaches it and how it should sit in the week. */
const LessonsTab: React.FC<LessonsTabProps> = ({ sessionId, overview, canWrite, onChanged }) => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [lessons, setLessons] = useState<Lesson[]>(overview.lessons);
  const [query, setQuery] = useState('');
  const [classId, setClassId] = useState<string | null>(null);
  useEffect(() => setLessons(overview.lessons), [overview.lessons]);
  const current = useRef(lessons);
  current.current = lessons;

  const weekly = overview.config ? weeklyPeriods(overview.config) : 0;

  const groups = useMemo<ClassGroup[]>(() => {
    const sectionsPerClass = new Map<string, number>();
    overview.sections.forEach((s) => sectionsPerClass.set(s.classId, (sectionsPerClass.get(s.classId) ?? 0) + 1));
    // Sections each curriculum entry reaches (a split subject has several lessons in one section).
    const perCurriculum = new Map<string, Set<string>>();
    lessons.forEach((l) => perCurriculum.set(l.curriculumId, (perCurriculum.get(l.curriculumId) ?? new Set<string>()).add(l.sectionKey)));

    const map = new Map<string, ClassGroup & { rowMap: Map<string, LessonRowData>; sectionMap: Map<string, { key: string; name: string | null; load: number }> }>();
    for (const l of lessons) {
      let g = map.get(l.classId);
      if (!g) {
        g = { classId: l.classId, label: fmtClassName(l.className), order: l.classOrder, sections: [], rows: [], rowMap: new Map(), sectionMap: new Map() };
        map.set(l.classId, g);
      }
      const sec = g.sectionMap.get(l.sectionKey) ?? { key: l.sectionKey, name: l.sectionName, load: 0 };
      sec.load += l.count;
      g.sectionMap.set(l.sectionKey, sec);

      let row = g.rowMap.get(l.curriculumId);
      if (!row) {
        const specific = (sectionsPerClass.get(l.classId) ?? 0) > 1 && perCurriculum.get(l.curriculumId)?.size === 1;
        row = {
          curriculumId: l.curriculumId, subjectId: l.subjectId, subjectName: l.subjectName, onlySection: specific ? l.sectionName : null, teachers: [],
          count: l.groupCount, doubles: l.groupDoubles, maxPerDay: l.maxPerDay, roomType: l.roomType, preferredTime: l.preferredTime,
        };
        g.rowMap.set(l.curriculumId, row);
      }
      row.teachers.push({ section: row.onlySection ? null : l.sectionName, name: l.teacherName, share: l.split ? l.count : null });
    }

    const q = query.trim().toLowerCase();
    return Array.from(map.values())
      .sort((a, b) => a.order - b.order)
      .map(({ rowMap, sectionMap, ...g }) => {
        const classHit = !q || g.label.toLowerCase().includes(q);
        const rows = Array.from(rowMap.values())
          .sort((a, b) => Number(!!a.onlySection) - Number(!!b.onlySection) || a.subjectName.localeCompare(b.subjectName))
          .filter((r) => classHit || r.subjectName.toLowerCase().includes(q) || r.teachers.some((t: LessonRowData['teachers'][number]) => t.name?.toLowerCase().includes(q)));
        return { ...g, rows, sections: Array.from(sectionMap.values()).sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')) };
      })
      .filter((g) => g.rows.length > 0);
  }, [lessons, overview.sections, query]);

  const change = useCallback(async (curriculumId: string, rules: LessonRules) => {
    const previous = current.current;
    setLessons(previous.map((l) => (l.curriculumId === curriculumId ? applyRules(l, rules) : l)));
    try {
      await timetableService.updateLesson(curriculumId, rules);
      onChanged();
    } catch (err: any) {
      setLessons(previous);
      enqueueSnackbar(err?.message || 'Could not save', { variant: 'error' });
    }
  }, [enqueueSnackbar, onChanged]);

  const selected = groups.find((g) => g.classId === classId) ?? groups[0] ?? null;

  const navItems: EntityItem[] = useMemo(() => groups.map((g) => {
    const over = g.sections.some((sec) => weekly > 0 && sec.load > weekly);
    const untaught = g.rows.some((r) => r.teachers.some((t) => !t.name));
    return {
      id: g.classId,
      label: g.label,
      detail: `${g.rows.length} subject${g.rows.length === 1 ? '' : 's'}${g.sections.length > 1 ? ` · ${g.sections.length} sections` : ''}`,
      alert: over ? 'More periods than the week holds' : untaught ? 'A subject has no teacher' : undefined,
      extra: weekly > 0 && <LoadBars sections={g.sections} weekly={weekly} compact />,
    };
  }), [groups, weekly]);

  if (!overview.lessons.length) {
    return (
      <EmptyState
        icon={<MenuBookOutlined />}
        title="No subjects to timetable yet"
        description="Lessons come from the session curriculum and the teachers in Teaching Loads. Add subjects to classes first."
        action={{ label: 'Open curriculum', onClick: () => navigate(fixLink('curriculum', sessionId).to) }}
      />
    );
  }

  return (
    <Box>
      <FilterBar>
        <TextField
          size="small" placeholder="Search classes, subjects or teachers" value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') setQuery(''); }}
          sx={{ flex: 1, minWidth: 240, maxWidth: 420 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment> }}
        />
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ ml: 'auto', color: 'text.secondary' }}>
          <CloudDoneOutlined sx={{ fontSize: 18 }} />
          <Typography variant="caption">Changes save as you make them</Typography>
        </Stack>
      </FilterBar>

      <TeacherLoadCard lessons={lessons} weekly={weekly} />

      {!selected ? (
        <EmptyState dense icon={<Search />} title="Nothing matches" description="Try another class, subject or teacher." />
      ) : (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '216px 1fr' }, alignItems: 'start' }}>
          <Paper variant="outlined" sx={{ borderRadius: '14px', p: 1.25, position: { lg: 'sticky' }, top: 72, maxHeight: { lg: 'calc(100vh - 96px)' }, display: 'flex', flexDirection: 'column' }}>
            <EntityList items={navItems} selected={selected.classId} onSelect={setClassId} placeholder="Find a class" hideSearch />
          </Paper>

          <Paper variant="outlined" sx={{ borderRadius: '14px', overflow: 'hidden' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" fontWeight={700} lineHeight={1.25}>{selected.label}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {selected.rows.length} subject{selected.rows.length === 1 ? '' : 's'}
                  {selected.sections.length > 1 ? ` across ${selected.sections.length} sections` : ''}
                  {' · '}a class-wide subject’s rules apply to every section
                </Typography>
              </Box>
              {weekly > 0 && <Box sx={{ width: { sm: 280 } }}><LoadBars sections={selected.sections} weekly={weekly} /></Box>}
            </Stack>
            <LessonHeader />
            {selected.rows.map((row) => (
              <LessonRow key={row.curriculumId} row={row} sessionId={sessionId} canWrite={canWrite} onChange={change} />
            ))}
          </Paper>
        </Box>
      )}
    </Box>
  );
};

/** Each section's periods against the week, as thin bars. */
const LoadBars = memo<{ sections: ClassGroup['sections']; weekly: number; compact?: boolean }>(({ sections, weekly, compact }) => (
  <Stack spacing={compact ? 0.5 : 0.75} sx={{ mt: compact ? 0.75 : 0 }}>
    {sections.map((sec) => {
      const over = sec.load > weekly;
      return (
        <Stack key={sec.key} direction="row" alignItems="center" spacing={1}>
          {sec.name && <Typography variant="caption" color="text.secondary" sx={{ width: compact ? 24 : 32, flexShrink: 0, fontSize: compact ? '0.66rem' : undefined }} noWrap>{sec.name}</Typography>}
          <LinearProgress
            variant="determinate"
            value={Math.min(100, (sec.load / weekly) * 100)}
            color={over ? 'error' : 'primary'}
            sx={(theme) => ({ flex: 1, height: compact ? 3 : 5, borderRadius: 3, bgcolor: alpha(theme.palette.text.primary, 0.07), '& .MuiLinearProgress-bar': { borderRadius: 3 } })}
          />
          {!compact && (
            <Typography variant="caption" sx={{ width: 64, textAlign: 'right', fontWeight: 600, color: over ? 'error.main' : 'text.secondary' }}>
              {sec.load} of {weekly}
            </Typography>
          )}
        </Stack>
      );
    })}
  </Stack>
));
LoadBars.displayName = 'LoadBars';

export default LessonsTab;
